import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Info, 
  Calculator, 
  ArrowRight,
  DollarSign,
  Users,
  Home,
  Baby,
  Heart,
  Briefcase
} from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Card } from '@/shared/components/ui/Card';
import { LoadingSpinner } from '@/shared/components/ui/LoadingSpinner';
import { ROUTES } from '@/shared/config/routes.config';
import { API_CONFIG } from '@/shared/config/api.config';
import { api } from '@/shared/services/apiClient';

interface BenefitType {
  id: string;
  name: string;
  description: string;
  category: string;
  rates: any;
}

const BenefitsInfoPage = () => {
  const [benefitTypes, setBenefitTypes] = useState<BenefitType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    fetchBenefitTypes();
  }, []);

  const fetchBenefitTypes = async () => {
    try {
      const response = await api.get<{ types: BenefitType[] }>(
        API_CONFIG.ENDPOINTS.BENEFITS.TYPES
      );
      setBenefitTypes(response.data?.types || []);
    } catch (error) {
      console.error('Failed to fetch benefit types:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', name: 'All Benefits', icon: DollarSign },
    { id: 'means_tested', name: 'Means-Tested', icon: Calculator },
    { id: 'disability', name: 'Disability', icon: Heart },
    { id: 'family', name: 'Family & Children', icon: Baby },
    { id: 'housing', name: 'Housing', icon: Home },
    { id: 'employment', name: 'Employment', icon: Briefcase }
  ];

  const filteredBenefits = selectedCategory === 'all' 
    ? benefitTypes 
    : benefitTypes.filter(b => b.category === selectedCategory);

  const getBenefitIcon = (benefitId: string) => {
    if (benefitId.includes('housing')) return Home;
    if (benefitId.includes('child') || benefitId.includes('family')) return Baby;
    if (benefitId.includes('disability') || benefitId.includes('pip')) return Heart;
    if (benefitId.includes('employment') || benefitId.includes('jsa')) return Briefcase;
    if (benefitId.includes('council')) return Home;
    return DollarSign;
  };

  const formatCurrency = (amount: number | undefined) => {
    if (!amount) return 'Variable';
    return `£${amount.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" label="Loading benefits information..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <section className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-neutral-900 mb-4">
              UK Benefits Guide
            </h1>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              Explore all available UK government benefits and understand what you might be eligible for. 
              Our comprehensive guide covers everything from Universal Credit to disability benefits.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-0 z-10 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map(category => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors
                    ${selectedCategory === category.id
                      ? 'bg-primary-600 text-white'
                      : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                    }
                  `}
                >
                  <Icon className="h-4 w-4" />
                  {category.name}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredBenefits.length === 0 ? (
            <div className="text-center py-12">
              <Info className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
              <p className="text-neutral-600">No benefits found in this category.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredBenefits.map(benefit => {
                const Icon = getBenefitIcon(benefit.id);
                return (
                  <Card key={benefit.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <Icon className="h-8 w-8 text-primary-600" />
                        <span className="text-xs font-medium text-neutral-500 uppercase">
                          {benefit.category.replace('_', ' ')}
                        </span>
                      </div>
                      
                      <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                        {benefit.name}
                      </h3>
                      
                      <p className="text-neutral-600 mb-4">
                        {benefit.description}
                      </p>

                      {benefit.rates && (
                        <div className="space-y-2 mb-4">
                          <div className="text-sm">
                            <span className="text-neutral-500">Standard Rate: </span>
                            <span className="font-medium text-neutral-900">
                              {formatCurrency(benefit.rates.standard)}/month
                            </span>
                          </div>
                          {benefit.rates.maximum && (
                            <div className="text-sm">
                              <span className="text-neutral-500">Maximum: </span>
                              <span className="font-medium text-neutral-900">
                                {formatCurrency(benefit.rates.maximum)}/month
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      <Link to={`${ROUTES.CALCULATOR.INDEX}?benefit=${benefit.id}`}>
                        <Button variant="outline" size="sm" className="w-full">
                          Calculate Eligibility
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-neutral-900 mb-4">
            Ready to Check Your Eligibility?
          </h2>
          <p className="text-lg text-neutral-600 mb-8">
            Use our intelligent calculator to see which benefits you qualify for and how much you could receive
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={ROUTES.CALCULATOR.INDEX}>
              <Button variant="primary" size="lg">
                Start Calculator
                <Calculator className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to={ROUTES.AUTH.REGISTER}>
              <Button variant="outline" size="lg">
                Create Account for Full Access
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BenefitsInfoPage;